import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api-client.config';

interface SetManualScoringResponse {
  success: boolean;
  questionId: string;
  enabled: boolean;
}

interface SetStatOverrideRequest {
  playerId?: string;
  teamId?: string;
  statType: string;
  value: number;
}

interface SetStatOverrideResponse {
  success: boolean;
  questionId: string;
}

/**
 * Set manual scoring for a question
 */
export const setQuestionManualScoring = async (
  questionId: string,
  enabled: boolean
): Promise<SetManualScoringResponse> => {
  return apiClient.post(
    '/api/admin/live-scoring/question/{questionId}/manual-scoring' as never,
    { enabled },
    { pathParams: { questionId } }
  ) as Promise<SetManualScoringResponse>;
};

/**
 * Override stat value for a question
 */
export const setStatOverride = async (
  questionId: string,
  data: SetStatOverrideRequest
): Promise<SetStatOverrideResponse> => {
  return apiClient.post(
    '/api/admin/live-scoring/question/{questionId}/stats/override' as never,
    data,
    { pathParams: { questionId } }
  ) as Promise<SetStatOverrideResponse>;
};

/**
 * Hook for setting manual scoring
 */
export const useSetManualScoringMutation = (questionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (enabled: boolean) => setQuestionManualScoring(questionId, enabled),
    onSuccess: () => {
      // Invalidate relevant queries if needed
      queryClient.invalidateQueries({ queryKey: ['questions'] });
    },
  });
};

/**
 * Hook for setting stat override
 */
export const useSetStatOverrideMutation = (questionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SetStatOverrideRequest) => setStatOverride(questionId, data),
    onSuccess: () => {
      // Invalidate relevant queries if needed
      queryClient.invalidateQueries({ queryKey: ['questions'] });
    },
  });
};
